import { Box } from '@mui/material';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Edge, useEdges, useReactFlow } from 'reactflow';
import useFlowsStore from '../../store/storeFlows';
import { useAuthStore } from '../../store/storeAuth';
import { nodeColorBasedOnType } from '../UI/Node/nodeConstants';
import { isActiveEdge } from '../../util/findActiveHandle';
import Node from '../UI/Node/Node';
import OutputTextarea from '../UI/OutputTextarea/OutputTextarea';
import type { ThirdPartyMode } from './types';
import {
  enrichMergedWithObjectDefaults,
  serializeNodeToBlobConfig,
  validateBlobConfigJson,
} from './nodeConfigUtils';
import ConnectionRequiredMessage from './common/ConnectionRequiredMessage';
import ConnectionStatus from './common/ConnectionStatus';
import NodeConfigBlobSection from './common/NodeConfigBlobSection';
import { useThirdPartyEdges } from './hooks/useThirdPartyEdges';
import { useThirdPartyFieldDebounce } from './hooks/useThirdPartyFieldDebounce';
import { useThirdPartyNodeState } from './hooks/useThirdPartyNodeState';
import { useThirdPartyPersist } from './hooks/useThirdPartyPersist';
import { useThirdPartySubmit } from './hooks/useThirdPartySubmit';
import ModeToggle from './common/ModeToggle';
import ProviderSelector from './common/ProviderSelector';
import PullModePanel from './common/PullModePanel';
import PushModePanel from './common/PushModePanel';

const ThirdPartyIntegrationNode: React.FC<{
  id: string;
  type: string;
  data: Record<string, unknown>;
}> = ({ id, type, data }) => {
  const { user } = useAuthStore(state => state);
  const { setNodeState, setEdges, setSaving, setSchema, flowId } =
    useFlowsStore(state => state);
  const flowCanvasPhase = useFlowsStore(state => state.flowCanvasPhase);
  const edges = useEdges();
  const reactFlow = useReactFlow();
  const nodes = reactFlow.getNodes();

  const blobEdges = useMemo(
    () =>
      (edges ?? []).filter(
        e => e.target === id && e.targetHandle === 'blobConfig',
      ),
    [edges, id],
  );

  const state = useThirdPartyNodeState(data);
  const {
    provider,
    setProviderState,
    mode,
    setMode,
    blobConfig,
    setBlobConfig,
    blobOpen,
    setBlobOpen,
    formValues,
    setFormValues,
    isLoading,
    setIsLoading,
    isChangingDirectly,
    setIsChangingDirectly,
    whoIsChanging,
    setWhoIsChanging,
    hasToExecute,
    setHasToExecute,
    validationErrorFieldName,
    setValidationErrorFieldName,
    userEditedFieldsRef,
    dataRef,
    formValuesRef,
    config,
    isNodeConnected,
  } = state;

  const nodeType =
    nodeColorBasedOnType[type as keyof typeof nodeColorBasedOnType];
  const findColor = nodeType?.colors ?? nodeColorBasedOnType.pullData?.colors;

  type SchemaSetter = (
    schema: Record<string, { type: string; required: boolean }>,
  ) => void;
  const { removeConnectedEdges, removeEdgesForField } = useThirdPartyEdges(
    edges,
    id,
    flowId,
    setEdges as (value: Edge[] | ((prev: Edge[]) => Edge[])) => void,
    setNodeState as (value: unknown) => void,
    setSaving,
    setSchema as SchemaSetter,
  );

  const { persist, persistWithBlob } = useThirdPartyPersist(
    edges,
    id,
    flowId,
    setNodeState as (value: unknown) => void,
    setSaving,
    setSchema as SchemaSetter,
    setBlobConfig,
  );

  useThirdPartyFieldDebounce(
    isChangingDirectly,
    whoIsChanging,
    setIsChangingDirectly,
    setWhoIsChanging,
    setFormValues,
    dataRef,
    formValuesRef,
    provider,
    mode,
    persistWithBlob,
    removeConnectedEdges,
    removeEdgesForField,
    userEditedFieldsRef,
  );

  useEffect(() => {
    if (blobEdges.length > 0) setBlobOpen(true);
  }, [blobEdges.length, setBlobOpen]);

  const { onSubmit } = useThirdPartySubmit(
    provider,
    config,
    mode,
    formValues,
    data,
    edges,
    id,
    flowId,
    setNodeState as (value: unknown) => void,
    setSaving,
    setSchema as SchemaSetter,
    setValidationErrorFieldName,
    setIsLoading,
    setHasToExecute,
  );

  const handleProviderChange = useCallback(
    async (providerId: string) => {
      await removeConnectedEdges();
      userEditedFieldsRef.current.clear();
      setProviderState(providerId);
      setFormValues(prev => ({ ...prev, provider: providerId }));
      const merged = { ...data, provider: providerId };
      persistWithBlob(merged, providerId, mode, { provider: providerId });
    },
    [removeConnectedEdges, persistWithBlob, data, mode],
  );

  const handleModeChange = useCallback(
    async (newMode: ThirdPartyMode) => {
      await removeConnectedEdges();
      userEditedFieldsRef.current.clear();
      setMode(newMode);
      const merged = { ...data, mode: newMode };
      persistWithBlob(merged, provider, newMode, { mode: newMode });
    },
    [removeConnectedEdges, persistWithBlob, data, provider],
  );

  const handleFieldChange = useCallback((name: string, value: unknown) => {
    setValidationErrorFieldName(prev => (prev === name ? null : prev));
    setFormValues(prev => ({ ...prev, [name]: value }));
    setWhoIsChanging({ name, value });
    setIsChangingDirectly(true);
  }, []);

  const handleBlobApply = useCallback(
    (parsed: Record<string, unknown>) => {
      const providerVal = parsed.provider as string;
      const modeVal = (parsed.mode as ThirdPartyMode) || 'pull';
      const integrationIdVal = parsed.integrationId as string | undefined;
      const integrationNameVal = parsed.integrationName as string | undefined;
      const {
        provider: _p,
        mode: _m,
        integrationId: _iid,
        integrationName: _iname,
        ...rest
      } = parsed;
      const restRecord = rest as Record<string, unknown>;
      const withDefaults = enrichMergedWithObjectDefaults(
        { ...restRecord },
        providerVal,
        modeVal,
      );
      setProviderState(providerVal);
      setMode(modeVal);
      userEditedFieldsRef.current.clear();
      setFormValues(withDefaults);
      const fullParsed = { ...parsed, ...withDefaults };
      const blobStr = JSON.stringify(fullParsed, null, 2);
      setBlobConfig(blobStr);
      persist({
        provider: providerVal,
        mode: modeVal,
        integrationId: integrationIdVal ?? null,
        integrationName: integrationNameVal ?? null,
        ...withDefaults,
        blobConfig: blobStr,
      });
    },
    [persist],
  );

  const handleConnectionSelected = useCallback(
    (integrationId: string, integrationName: string) => {
      const payload = {
        integrationId,
        integrationName,
        connectedByUserId: user?.id ?? null,
        connectedByUserName:
          (user as { name?: string })?.name ??
          (user as { email?: string })?.email ??
          '',
      };
      const merged = { ...data, provider, mode, ...formValues, ...payload };
      persistWithBlob(merged, provider, mode, { ...merged });
    },
    [user, persistWithBlob, data, provider, mode, formValues],
  );

  const handleIntegrationDisconnected = useCallback(() => {
    const payload = {
      integrationId: null,
      integrationName: null,
      connectedByUserId: null,
      connectedByUserName: null,
    };
    const merged = { ...data, provider, mode, ...formValues, ...payload };
    persistWithBlob(merged, provider, mode, { ...merged });
  }, [persistWithBlob, data, provider, mode, formValues]);

  const [blobParseError, setBlobParseError] = useState<string | null>(null);
  const blobDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastAppliedBlobRef = useRef<string>('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flowTrigger = data?.flowTrigger as { id?: string } | undefined;

  useEffect(() => {
    const raw =
      typeof data.blobConfig === 'string' ? data.blobConfig.trim() : '';
    if (!raw) {
      return;
    }
    const seeded = validateBlobConfigJson(raw);
    if (!seeded.ok) {
      return;
    }
    lastAppliedBlobRef.current = JSON.stringify(seeded.parsed, null, 2);
  }, [id, data.blobConfig]);

  useEffect(() => {
    if (blobDebounceRef.current) clearTimeout(blobDebounceRef.current);
    const value = blobConfig?.trim() ?? '';
    if (!value) {
      const emptyResult = validateBlobConfigJson('');
      setBlobParseError(emptyResult.ok ? null : emptyResult.error);
      return;
    }
    if (isChangingDirectly) return;
    blobDebounceRef.current = setTimeout(() => {
      blobDebounceRef.current = null;
      if (useFlowsStore.getState().flowCanvasPhase !== 'ready') {
        return;
      }
      const result = validateBlobConfigJson(blobConfig);
      if (!result.ok) {
        setBlobParseError(result.error || null);
        return;
      }
      setBlobParseError(null);
      const parsed = result.parsed;
      const parsedBlobStr = JSON.stringify(parsed, null, 2);
      if (parsedBlobStr === lastAppliedBlobRef.current) return;
      const currentFormValues = formValuesRef.current;
      const currentMerged = { ...data, provider, mode, ...currentFormValues };
      const currentBlobStr = serializeNodeToBlobConfig(currentMerged);
      if (currentBlobStr === parsedBlobStr) return;
      try {
        const currentParsed = JSON.parse(currentBlobStr) as Record<
          string,
          unknown
        >;
        const keys = new Set([
          ...Object.keys(currentParsed),
          ...Object.keys(parsed),
        ]);
        let hasChange = false;
        for (const k of keys) {
          const a = currentParsed[k];
          const b = parsed[k];
          if (JSON.stringify(a) !== JSON.stringify(b)) {
            hasChange = true;
            break;
          }
        }
        if (!hasChange) return;
      } catch {
        /* ignore compare */
      }
      lastAppliedBlobRef.current = parsedBlobStr;
      handleBlobApply(parsed);
    }, 500);
    return () => {
      if (blobDebounceRef.current) clearTimeout(blobDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    blobConfig,
    provider,
    mode,
    isChangingDirectly,
    handleBlobApply,
    flowCanvasPhase,
  ]);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (flowTrigger?.id && !data?.canceled && !data?.paused) {
      if (hasToExecute) {
        debounceRef.current = setTimeout(() => {
          setHasToExecute(false);
          onSubmit();
        }, 1000);
      }
    }
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [hasToExecute, formValues, data]);

  const handleRun = useCallback(
    (run: string) => {
      persist({ run });
    },
    [persist],
  );

  const outputValue = (data?.output as string) ?? '';

  return (
    <Node
      onSubmit={onSubmit}
      btnText={mode === 'pull' ? 'Get Data' : 'Push'}
      handleRun={handleRun}
      data={data}
      type={type}
      id={id}
      isLoading={isLoading}
      edges={edges}
      showTokensUsed={false}
    >
      <Box>
        <Box display="flex">
          <Box
            p="16px"
            borderRight={`1px solid ${
              user?.theme === 'dark' ? '#475467' : '#EAECF0'
            }`}
            sx={{
              backgroundColor: 'white',
              minWidth: 320,
              maxWidth: 360,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <ProviderSelector
                value={provider}
                onChange={handleProviderChange}
              />
              {provider && (
                <ConnectionStatus
                  provider={provider}
                  config={config ?? undefined}
                  onConnectionSelected={handleConnectionSelected}
                  onDisconnected={handleIntegrationDisconnected}
                  selectedIntegrationId={
                    data.integrationId as string | undefined
                  }
                  currentUserId={user?.id}
                />
              )}
              {provider && (
                <ModeToggle
                  mode={mode}
                  onChange={handleModeChange}
                  config={config}
                />
              )}
            </Box>

            {provider && config && (
              <>
                {isNodeConnected || config.authMethod === 'apiKey' ? (
                  <Box sx={{ mt: 2 }}>
                    {mode === 'pull' && config.supportsPull && (
                      <PullModePanel
                        config={config}
                        formValues={formValues}
                        onChange={handleFieldChange}
                        nodeId={id}
                        provider={provider}
                        connected={isNodeConnected}
                        nodeData={data}
                        errorFieldName={validationErrorFieldName}
                      />
                    )}
                    {mode === 'push' && config.supportsPush && (
                      <PushModePanel
                        config={config}
                        formValues={formValues}
                        onChange={handleFieldChange}
                        nodeId={id}
                        provider={provider}
                        connected={isNodeConnected}
                        nodeData={data}
                        errorFieldName={validationErrorFieldName}
                      />
                    )}
                  </Box>
                ) : (
                  <ConnectionRequiredMessage providerName={config.name} />
                )}

                <NodeConfigBlobSection
                  blobOpen={blobOpen}
                  onToggle={() => setBlobOpen(prev => !prev)}
                  blobConfig={blobConfig}
                  onChange={setBlobConfig}
                  parseError={blobParseError}
                  nodeId={id}
                  edges={edges}
                  findColor={findColor}
                  nodes={nodes}
                />
              </>
            )}
          </Box>
          <Box p="16px 24px 16px 16px">
            <OutputTextarea
              previewIndex={data.previewIndex as number | undefined}
              value={outputValue}
              activeHandle={isActiveEdge(edges, id, 'output', 'source')}
              placement={data?.placement as string | undefined}
              labelName="Output"
              findColor={findColor}
              nodeLabel={data?.label as string | undefined}
              nodeId={id}
              inputSize={data?.outputSize}
              collapsed={Boolean(data?.collapsed)}
            />
          </Box>
        </Box>
      </Box>
    </Node>
  );
};

export default memo(ThirdPartyIntegrationNode);
